import { fetchWithAuth, BASE_URL } from './client';
import { logError } from '../../shared/utils/logger';
import { getTokens } from '../../shared/utils/secureTokenStorage';
import { getOrgSlugFromHostname } from './sso';

// ==================== ORGANIZATION CRUD ====================

/**
 * Create a new organization
 * POST /api/organizations
 */
export async function createOrganization(payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });
    if (!response.ok) {
        logError('Failed to create organization', response.status);
        const error = new Error('Failed to create organization');
        error.status = response.status;
        throw error;
    }
    return response.json();
}

/**
 * Fetch organization details
 * GET /api/organizations/{orgId}
 */
export async function fetchOrganization(orgId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}`, {
        cache: 'no-store'
    });
    if (response.status === 404) {
        return { data: null };
    }
    if (!response.ok) {
        logError('Failed to fetch organization', response.status);
        throw new Error('Failed to fetch organization');
    }
    return response.json();
}

/**
 * Fetch current user's organization (if any)
 * GET /api/organizations/me
 *
 * NOTE: On subdomain portals, use fetchCurrentOrganization() instead
 * to ensure the correct org is fetched based on login context.
 */
export async function fetchMyOrganization() {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/me`, {
        cache: 'no-store'
    });
    if (response.status === 404) {
        return { data: null };
    }
    if (!response.ok) {
        logError('Failed to fetch user organization', response.status);
        throw new Error('Failed to fetch organization');
    }
    try {
        return await response.json();
    } catch {
        return { data: null };
    }
}

/**
 * Fetch the current organization based on context:
 * - On subdomain portal: Uses org_id from login response (stored in tokens)
 * - On main site: Falls back to GET /api/organizations/me
 *
 * This ensures users on subdomain portals see only the org they logged into,
 * not a random org from their list of memberships.
 */
export async function fetchCurrentOrganization() {
    const isSubdomainPortal = getOrgSlugFromHostname() !== null;

    if (isSubdomainPortal) {
        // On subdomain portal - use org_id from login response
        const tokens = getTokens();
        const orgId = tokens?.org_id;

        if (orgId) {
            // Fetch this specific org
            const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}`, {
                cache: 'no-store'
            });
            if (response.status === 404) {
                return { data: null };
            }
            if (!response.ok) {
                logError('Failed to fetch organization', response.status);
                throw new Error('Failed to fetch organization');
            }
            try {
                const orgData = await response.json();
                // Attach the org context from tokens for role info
                if (orgData && !orgData.user_role && tokens?.org_role) {
                    orgData.user_role = tokens.org_role;
                }
                return { data: orgData };
            } catch {
                return { data: null };
            }
        }

        // Fallback: No org_id in tokens (shouldn't happen on valid subdomain login)
        logError('Subdomain portal but no org_id in tokens');
    }

    // Main site - use the standard endpoint
    return fetchMyOrganization();
}

/**
 * Get the org context stored from subdomain login
 * Returns { org_id, org_name, org_role, org_slug } or null
 */
export function getStoredOrgContext() {
    const tokens = getTokens();
    if (tokens?.org_id) {
        return {
            org_id: tokens.org_id,
            org_name: tokens.org_name,
            org_role: tokens.org_role,
            org_slug: tokens.org_slug
        };
    }
    return null;
}

/**
 * Check if we're on a subdomain portal
 */
export function isOnSubdomainPortal() {
    return getOrgSlugFromHostname() !== null;
}

/**
 * Update organization details
 * PATCH /api/organizations/{orgId}
 */
export async function updateOrganization(orgId, updates) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
    });
    if (!response.ok) {
        logError('Failed to update organization', response.status);
        throw new Error('Failed to update organization');
    }
    return response.json();
}

/**
 * Delete organization
 * DELETE /api/organizations/{orgId}
 */
export async function deleteOrganization(orgId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}`, {
        method: 'DELETE'
    });
    if (!response.ok) {
        logError('Failed to delete organization', response.status);
        throw new Error('Failed to delete organization');
    }
    if (response.status === 204) {
        return { success: true };
    }
    try {
        return await response.json();
    } catch {
        return { success: true };
    }
}

// ==================== WORKSPACES ====================

/**
 * Fetch all workspaces in an organization
 * GET /api/organizations/{orgId}/workspaces
 */
export async function fetchWorkspaces(orgId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/workspaces`, {
        cache: 'no-store'
    });
    if (response.status === 404) {
        return { data: { workspaces: [] } };
    }
    if (!response.ok) {
        logError('Failed to fetch workspaces', response.status);
        throw new Error('Failed to fetch workspaces');
    }
    try {
        return await response.json();
    } catch {
        return { data: { workspaces: [] } };
    }
}

/**
 * Create a new workspace
 * POST /api/organizations/{orgId}/workspaces
 */
export async function createWorkspace(orgId, payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/workspaces`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });
    if (!response.ok) {
        if (response.status === 403) {
            const errorData = await response.json();
            const message = errorData?.detail?.message || "Workspace limit reached for your plan.";
            throw new Error(message);
        }
        logError('Failed to create workspace', response.status);
        throw new Error('Failed to create workspace');
    }
    return response.json();
}

/**
 * Get workspace details
 * GET /api/organizations/{orgId}/workspaces/{wsId}
 */
export async function fetchWorkspace(orgId, wsId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/workspaces/${wsId}`, {
        cache: 'no-store'
    });
    if (!response.ok) {
        logError('Failed to fetch workspace', response.status);
        throw new Error('Failed to fetch workspace');
    }
    return response.json();
}

/**
 * Update workspace
 * PATCH /api/organizations/{orgId}/workspaces/{wsId}
 */
export async function updateWorkspace(orgId, wsId, updates) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/workspaces/${wsId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
    });
    if (!response.ok) {
        logError('Failed to update workspace', response.status);
        throw new Error('Failed to update workspace');
    }
    return response.json();
}

/**
 * Delete workspace
 * DELETE /api/organizations/{orgId}/workspaces/{wsId}
 */
export async function deleteWorkspace(orgId, wsId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/workspaces/${wsId}`, {
        method: 'DELETE'
    });
    if (!response.ok) {
        logError('Failed to delete workspace', response.status);
        throw new Error('Failed to delete workspace');
    }
    if (response.status === 204) {
        return { success: true };
    }
    try {
        return await response.json();
    } catch {
        return { success: true };
    }
}

// ==================== GROUPS ====================

/**
 * Fetch all groups in a workspace
 * GET /api/organizations/{orgId}/workspaces/{wsId}/groups
 */
export async function fetchGroups(orgId, wsId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/workspaces/${wsId}/groups`, {
        cache: 'no-store'
    });
    if (response.status === 404) {
        return { data: { groups: [] } };
    }
    if (!response.ok) {
        logError('Failed to fetch groups', response.status);
        throw new Error('Failed to fetch groups');
    }
    try {
        return await response.json();
    } catch {
        return { data: { groups: [] } };
    }
}

/**
 * Create a new group
 * POST /api/organizations/{orgId}/workspaces/{wsId}/groups
 */
export async function createGroup(orgId, wsId, payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/workspaces/${wsId}/groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });
    if (!response.ok) {
        if (response.status === 403) {
            const errorData = await response.json();
            const message = errorData?.detail?.message || "Group limit reached for your plan.";
            throw new Error(message);
        }
        logError('Failed to create group', response.status);
        throw new Error('Failed to create group');
    }
    return response.json();
}

/**
 * Get group details
 * GET /api/organizations/{orgId}/workspaces/{wsId}/groups/{groupId}
 */
export async function fetchGroup(orgId, wsId, groupId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/workspaces/${wsId}/groups/${groupId}`, {
        cache: 'no-store'
    });
    if (!response.ok) {
        logError('Failed to fetch group', response.status);
        throw new Error('Failed to fetch group');
    }
    return response.json();
}

/**
 * Update group
 * PATCH /api/organizations/{orgId}/workspaces/{wsId}/groups/{groupId}
 */
export async function updateGroup(orgId, wsId, groupId, updates) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/workspaces/${wsId}/groups/${groupId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
    });
    if (!response.ok) {
        logError('Failed to update group', response.status);
        throw new Error('Failed to update group');
    }
    return response.json();
}

/**
 * Delete group
 * DELETE /api/organizations/{orgId}/workspaces/{wsId}/groups/{groupId}
 */
export async function deleteGroup(orgId, wsId, groupId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/workspaces/${wsId}/groups/${groupId}`, {
        method: 'DELETE'
    });
    if (!response.ok) {
        logError('Failed to delete group', response.status);
        throw new Error('Failed to delete group');
    }
    if (response.status === 204) {
        return { success: true };
    }
    try {
        return await response.json();
    } catch {
        return { success: true };
    }
}

// ==================== MEMBERS ====================

/**
 * Fetch all members in an organization
 * GET /api/organizations/{orgId}/members
 */
export async function fetchOrgMembers(orgId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/members`, {
        cache: 'no-store'
    });
    if (response.status === 404) {
        return { data: { members: [] } };
    }
    if (!response.ok) {
        logError('Failed to fetch organization members', response.status);
        throw new Error('Failed to fetch organization members');
    }
    try {
        return await response.json();
    } catch {
        return { data: { members: [] } };
    }
}

/**
 * Invite a member to the organization
 * POST /api/organizations/{orgId}/members
 */
export async function inviteOrgMember(orgId, payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });
    if (!response.ok) {
        if (response.status === 403) {
            const errorData = await response.json();
            const message = errorData?.detail?.message || "Member limit reached for your plan.";
            throw new Error(message);
        }
        if (response.status === 409) {
            throw new Error("This user is already a member of the organization.");
        }
        logError('Failed to invite member', response.status);
        throw new Error('Failed to invite member');
    }
    return response.json();
}

/**
 * Update member role
 * PATCH /api/organizations/{orgId}/members/{userId}
 */
export async function updateMemberRole(orgId, userId, updates) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/members/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
    });
    if (!response.ok) {
        logError('Failed to update member role', response.status);
        throw new Error('Failed to update member role');
    }
    return response.json();
}

/**
 * Resend invitation to a pending member
 * POST /api/organizations/{orgId}/members/{memberId}/resend-invite
 */
export async function resendMemberInvite(orgId, memberId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/members/${memberId}/resend-invite`, {
        method: 'POST'
    });
    if (!response.ok) {
        logError('Failed to resend invitation', response.status);
        throw new Error('Failed to resend invitation');
    }
    return response.json();
}

/**
 * Remove a member from the organization
 * DELETE /api/organizations/{orgId}/members/{userId}
 */
export async function removeOrgMember(orgId, userId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/members/${userId}`, {
        method: 'DELETE'
    });
    if (!response.ok) {
        logError('Failed to remove member', response.status);
        throw new Error('Failed to remove member');
    }
    if (response.status === 204) {
        return { success: true };
    }
    try {
        return await response.json();
    } catch {
        return { success: true };
    }
}

/**
 * Fetch members in a specific workspace
 * GET /api/organizations/{orgId}/workspaces/{wsId}/members
 */
export async function fetchWorkspaceMembers(orgId, wsId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/workspaces/${wsId}/members`, {
        cache: 'no-store'
    });
    if (response.status === 404) {
        return { data: { members: [] } };
    }
    if (!response.ok) {
        logError('Failed to fetch workspace members', response.status);
        throw new Error('Failed to fetch workspace members');
    }
    try {
        return await response.json();
    } catch {
        return { data: { members: [] } };
    }
}

/**
 * Add member to workspace
 * POST /api/organizations/{orgId}/workspaces/{wsId}/members
 */
export async function addWorkspaceMember(orgId, wsId, payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/workspaces/${wsId}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });
    if (!response.ok) {
        logError('Failed to add workspace member', response.status);
        throw new Error('Failed to add workspace member');
    }
    return response.json();
}

/**
 * Remove member from workspace
 * DELETE /api/organizations/{orgId}/workspaces/{wsId}/members/{userId}
 */
export async function removeWorkspaceMember(orgId, wsId, userId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/workspaces/${wsId}/members/${userId}`, {
        method: 'DELETE'
    });
    if (!response.ok) {
        logError('Failed to remove workspace member', response.status);
        throw new Error('Failed to remove workspace member');
    }
    if (response.status === 204) {
        return { success: true };
    }
    try {
        return await response.json();
    } catch {
        return { success: true };
    }
}

/**
 * Fetch members in a specific group
 * GET /api/organizations/{orgId}/workspaces/{wsId}/groups/{groupId}/members
 */
export async function fetchGroupMembers(orgId, wsId, groupId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/workspaces/${wsId}/groups/${groupId}/members`, {
        cache: 'no-store'
    });
    if (response.status === 404) {
        return { data: { members: [] } };
    }
    if (!response.ok) {
        logError('Failed to fetch group members', response.status);
        throw new Error('Failed to fetch group members');
    }
    try {
        return await response.json();
    } catch {
        return { data: { members: [] } };
    }
}

/**
 * Add member to group
 * POST /api/organizations/{orgId}/workspaces/{wsId}/groups/{groupId}/members
 */
export async function addGroupMember(orgId, wsId, groupId, payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/workspaces/${wsId}/groups/${groupId}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });
    if (!response.ok) {
        logError('Failed to add group member', response.status);
        throw new Error('Failed to add group member');
    }
    return response.json();
}

/**
 * Remove member from group
 * DELETE /api/organizations/{orgId}/workspaces/{wsId}/groups/{groupId}/members/{userId}
 */
export async function removeGroupMember(orgId, wsId, groupId, userId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/workspaces/${wsId}/groups/${groupId}/members/${userId}`, {
        method: 'DELETE'
    });
    if (!response.ok) {
        logError('Failed to remove group member', response.status);
        throw new Error('Failed to remove group member');
    }
    if (response.status === 204) {
        return { success: true };
    }
    try {
        return await response.json();
    } catch {
        return { success: true };
    }
}

// ==================== API KEYS ====================

/**
 * Fetch organization API keys
 * GET /api/organizations/{orgId}/api-keys
 */
export async function fetchOrgApiKeys(orgId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/api-keys`, {
        cache: 'no-store'
    });
    if (response.status === 404) {
        return { data: { keys: [] } };
    }
    if (!response.ok) {
        logError('Failed to fetch organization API keys', response.status);
        throw new Error('Failed to fetch API keys');
    }
    try {
        return await response.json();
    } catch {
        return { data: { keys: [] } };
    }
}

/**
 * Create organization API key
 * POST /api/organizations/{orgId}/api-keys
 */
export async function createOrgApiKey(orgId, payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/api-keys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });
    if (!response.ok) {
        logError('Failed to create organization API key', response.status);
        throw new Error('Failed to create API key');
    }
    return response.json();
}

/**
 * Delete organization API key
 * DELETE /api/organizations/{orgId}/api-keys/{keyId}
 */
export async function deleteOrgApiKey(orgId, keyId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/api-keys/${keyId}`, {
        method: 'DELETE'
    });
    if (!response.ok) {
        logError('Failed to delete organization API key', response.status);
        throw new Error('Failed to delete API key');
    }
    if (response.status === 204) {
        return { success: true };
    }
    try {
        return await response.json();
    } catch {
        return { success: true };
    }
}

/**
 * Rotate organization API key
 * POST /api/organizations/{orgId}/api-keys/{keyId}/rotate
 */
export async function rotateOrgApiKey(orgId, keyId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/api-keys/${keyId}/rotate`, {
        method: 'POST'
    });
    if (!response.ok) {
        logError('Failed to rotate organization API key', response.status);
        throw new Error('Failed to rotate API key');
    }
    return response.json();
}

// ==================== WEBHOOKS ====================

/**
 * Fetch organization webhooks
 * GET /api/organizations/{orgId}/webhooks
 */
export async function fetchOrgWebhooks(orgId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/webhooks`, {
        cache: 'no-store'
    });
    if (response.status === 404) {
        return { data: { webhooks: [] } };
    }
    if (!response.ok) {
        logError('Failed to fetch organization webhooks', response.status);
        throw new Error('Failed to fetch webhooks');
    }
    try {
        return await response.json();
    } catch {
        return { data: { webhooks: [] } };
    }
}

/**
 * Create organization webhook
 * POST /api/organizations/{orgId}/webhooks
 */
export async function createOrgWebhook(orgId, payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/webhooks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });
    if (!response.ok) {
        logError('Failed to create organization webhook', response.status);
        throw new Error('Failed to create webhook');
    }
    return response.json();
}

/**
 * Update organization webhook
 * PATCH /api/organizations/{orgId}/webhooks/{webhookId}
 */
export async function updateOrgWebhook(orgId, webhookId, updates) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/webhooks/${webhookId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
    });
    if (!response.ok) {
        logError('Failed to update organization webhook', response.status);
        throw new Error('Failed to update webhook');
    }
    return response.json();
}

/**
 * Delete organization webhook
 * DELETE /api/organizations/{orgId}/webhooks/{webhookId}
 */
export async function deleteOrgWebhook(orgId, webhookId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/webhooks/${webhookId}`, {
        method: 'DELETE'
    });
    if (!response.ok) {
        logError('Failed to delete organization webhook', response.status);
        throw new Error('Failed to delete webhook');
    }
    if (response.status === 204) {
        return { success: true };
    }
    try {
        return await response.json();
    } catch {
        return { success: true };
    }
}

/**
 * Test organization webhook
 * POST /api/organizations/{orgId}/webhooks/{webhookId}/test
 */
export async function testOrgWebhook(orgId, webhookId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/webhooks/${webhookId}/test`, {
        method: 'POST'
    });
    if (!response.ok) {
        logError('Failed to test organization webhook', response.status);
        throw new Error('Failed to test webhook');
    }
    return response.json();
}

/**
 * Fetch webhook delivery logs
 * GET /api/organizations/{orgId}/webhooks/{webhookId}/deliveries
 */
export async function fetchWebhookDeliveries(orgId, webhookId, params = {}) {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.cursor) queryParams.append('cursor', params.cursor);

    const url = `${BASE_URL}/api/organizations/${orgId}/webhooks/${webhookId}/deliveries${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await fetchWithAuth(url, { cache: 'no-store' });

    if (response.status === 404) {
        return { data: { deliveries: [] } };
    }
    if (!response.ok) {
        logError('Failed to fetch webhook deliveries', response.status);
        throw new Error('Failed to fetch webhook deliveries');
    }
    try {
        return await response.json();
    } catch {
        return { data: { deliveries: [] } };
    }
}

// ==================== ROLES ====================

/**
 * Fetch organization roles
 * GET /api/organizations/{orgId}/roles?include_system=true
 */
export async function fetchOrgRoles(orgId, includeSystem = true) {
    const url = `${BASE_URL}/api/organizations/${orgId}/roles${includeSystem ? '?include_system=true' : ''}`;
    const response = await fetchWithAuth(url, { cache: 'no-store' });
    if (response.status === 404) {
        return { roles: [] };
    }
    if (!response.ok) {
        logError('Failed to fetch organization roles', response.status);
        throw new Error('Failed to fetch roles');
    }
    try {
        return await response.json();
    } catch {
        return { roles: [] };
    }
}

/**
 * Create custom role (Enterprise only)
 * POST /api/organizations/{orgId}/roles
 */
export async function createOrgRole(orgId, payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/roles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });
    if (!response.ok) {
        logError('Failed to create role', response.status);
        throw new Error('Failed to create role');
    }
    return response.json();
}

/**
 * Delete custom role
 * DELETE /api/organizations/{orgId}/roles/{roleId}
 */
export async function deleteOrgRole(orgId, roleId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/roles/${roleId}`, {
        method: 'DELETE'
    });
    if (!response.ok) {
        logError('Failed to delete role', response.status);
        throw new Error('Failed to delete role');
    }
    if (response.status === 204) {
        return { success: true };
    }
    try {
        return await response.json();
    } catch {
        return { success: true };
    }
}

// ==================== USER CONTEXT ====================

/**
 * Get current user's context in organization
 * GET /api/organizations/{orgId}/context
 */
export async function fetchOrgContext(orgId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/context`, {
        cache: 'no-store'
    });
    if (!response.ok) {
        logError('Failed to fetch organization context', response.status);
        throw new Error('Failed to fetch context');
    }
    return response.json();
}

// ==================== OWNERSHIP ====================

/**
 * Transfer organization ownership
 * POST /api/organizations/{orgId}/transfer-ownership
 */
export async function transferOrgOwnership(orgId, newOwnerId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations/${orgId}/transfer-ownership`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_owner_id: newOwnerId })
    });
    if (!response.ok) {
        logError('Failed to transfer ownership', response.status);
        throw new Error('Failed to transfer ownership');
    }
    return response.json();
}

// ==================== LIST ORGANIZATIONS ====================

/**
 * List all organizations user belongs to
 * GET /api/organizations
 */
export async function fetchOrganizations() {
    const response = await fetchWithAuth(`${BASE_URL}/api/organizations`, {
        cache: 'no-store'
    });
    if (!response.ok) {
        logError('Failed to fetch organizations', response.status);
        throw new Error('Failed to fetch organizations');
    }
    try {
        return await response.json();
    } catch {
        return { organizations: [], total: 0 };
    }
}
